import { withCountryCode } from "../utils/phones";
/**
 * Shared plumbing for the two documents.
 *
 * The proposal and the quotation are separate documents with separate section
 * lists, but they price the same quote and print the same company — so the
 * derivations and the formatting live here rather than being written twice and
 * drifting apart.
 */

export const inr = (n) =>
  n === null || n === undefined ? "—" : `₹${Math.round(Number(n)).toLocaleString("en-IN")}`;

export const num = (n) =>
  n === null || n === undefined ? "—" : Number(n).toLocaleString("en-IN");

export const fmtDate = (d) =>
  d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

/**
 * A company field is printed only when it has one.
 *
 * The API sends null for anything not filled in, and `{company.sac}` in JSX
 * happily rendered the string "null" onto a client-facing document.
 */
export const has = (v) =>
  v !== null && v !== undefined && String(v).trim() !== "" && String(v).trim() !== "null";

/** `SAC 998314`, or nothing at all. */
export const labelled = (label, value) => (has(value) ? `${label} ${value}` : "");

/** Joins the parts that survived, so no stray separators are left behind. */
export const joinParts = (parts, sep = " · ") => parts.filter(Boolean).join(sep);

/** Rupees in words — printed under the quotation total, as invoices do. */
export const words = (value) => {
  let n = Math.round(Number(value) || 0);
  if (n === 0) return "Zero";
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (x) => (x < 20 ? a[x] : b[Math.floor(x / 10)] + (x % 10 ? ` ${a[x % 10]}` : ""));
  const three = (x) =>
    (Math.floor(x / 100) ? `${a[Math.floor(x / 100)]} Hundred${x % 100 ? " " : ""}` : "") +
    (x % 100 ? two(x % 100) : "");
  let s = "";
  const cr = Math.floor(n / 1e7); n %= 1e7;
  const la = Math.floor(n / 1e5); n %= 1e5;
  const th = Math.floor(n / 1e3); n %= 1e3;
  if (cr) s += `${three(cr)} Crore `;
  if (la) s += `${three(la)} Lakh `;
  if (th) s += `${three(th)} Thousand `;
  if (n) s += three(n);
  return s.trim();
};

/* Template copy arrives as HTML strings (it carries <b>/<i> emphasis), so it is
   rendered as markup. It comes from our own backend, not from user input, which
   is what makes that safe. */
export const Html = ({ as: Tag = "span", html, ...rest }) => (
  <Tag {...rest} dangerouslySetInnerHTML={{ __html: html || "" }} />
);

/** Initials for the founder avatar when no photo is configured. */
export const initials = (name = "") =>
  name.trim().split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?";

/**
 * The signature and company seal.
 *
 * The drawn squiggle is a stand-in — pass `imageUrl` to print a scan of the real
 * signature instead. The seal stays drawn, and carries the company's own name
 * and city, so it follows the Company Profile rather than being frozen into the
 * artwork; keep `city` short or the circular path clips it.
 */
export const Signature = ({ sealId, imageUrl, legalName, city }) => (
  <>
    {imageUrl ? (
      <img className="sig-draw" src={imageUrl} alt="Signature" style={{ objectFit: "contain" }} />
    ) : (
      <svg className="sig-draw" viewBox="0 0 300 108" fill="none" stroke="#12245C" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14,76 C9,40 33,16 42,40 C50,60 22,58 31,61 C46,61 40,77 56,71 C68,67 60,42 74,53 C87,63 79,27 88,29 C94,53 92,74 106,67 C118,61 111,43 122,55 C131,65 142,67 152,58" />
        <path d="M150,60 C150,32 167,25 179,44 C190,62 159,74 150,65 C172,67 188,57 198,64 C208,71 203,50 212,58 C220,66 210,76 222,71 C236,67 250,60 270,68" />
        <path d="M8,92 C74,80 154,100 226,84 C254,78 274,87 294,81" strokeWidth="2.2" opacity=".85" />
      </svg>
    )}
    <svg className="seal-svg" viewBox="0 0 100 100">
      <defs>
        <path id={`${sealId}a`} d="M12,50 a38,38 0 1,1 76,0" />
        <path id={`${sealId}b`} d="M17,50 a33,33 0 1,0 66,0" />
      </defs>
      <g fill="none" stroke="#12245C" strokeOpacity=".78">
        <circle cx="50" cy="50" r="47" strokeWidth="1.6" />
        <circle cx="50" cy="50" r="40.5" strokeWidth="1" />
      </g>
      <g fill="#12245C" fillOpacity=".85" fontFamily="Arial,Helvetica,sans-serif">
        <text fontSize="6.7" fontWeight="700" letterSpacing="0.5" textAnchor="middle">
          <textPath href={`#${sealId}a`} startOffset="50%">{(legalName || "").toUpperCase()}</textPath>
        </text>
        <text fontSize="5.6" fontWeight="700" letterSpacing="1" textAnchor="middle">
          <textPath href={`#${sealId}b`} startOffset="50%">{(city || "").toUpperCase()}</textPath>
        </text>
        <text x="50" y="43" fontSize="6.4" fontWeight="800" textAnchor="middle">AUTHORISED</text>
        <text x="50" y="51" fontSize="6.4" fontWeight="800" textAnchor="middle">SIGNATORY</text>
        <text x="50" y="64" fontSize="12" textAnchor="middle">★</text>
      </g>
    </svg>
  </>
);

/** How a billing term reads once the API has told us how many months it is. */
const TERM = {
  1: { label: "Monthly", word: "month", first: "Month 1", days: 30, grace: 5 },
  3: { label: "Quarterly", word: "quarter", first: "Q1", days: 90, grace: 7 },
  12: { label: "Annual", word: "year", first: "Year 1", days: 365, grace: 14 },
};
const FREQ_MONTHS = { monthly: 1, quarterly: 3, annual: 12 };

/**
 * Everything both documents derive from the same quote: the term, the dates,
 * the audience line, and the placeholder filler.
 */
export const deriveBasics = ({ form, quote, template, segmentLabel }) => {
  const company = template?.company || {};
  const client = form.client_name?.trim() || "Your Company";
  const seg = quote?.segment || form.segment;

  /* A custom segment has no shipped audience line — it is named after itself,
     which is what the admin who created it chose to call it. */
  const audience =
    seg === "corporate" ? "Corporates & Brand Teams"
    : seg === "attorney" ? "Attorneys & Law Firms"
    : segmentLabel || seg;

  const today = new Date();
  const validity = template?.validity_days ?? 30;
  const validUntil = new Date(today.getTime() + validity * 864e5);

  /* Past the enterprise threshold the API returns no pricing at all — both
     documents degrade to "talk to sales" rather than showing zeros. */
  const isCustom = quote?.custom === true;
  const p = quote?.pricing || {};
  const li = quote?.line_items || {};
  const d = quote?.discounts || {};
  /* The term is the API's to decide — it is what the invoice was actually
     calculated over. FREQUENCY is only the fallback before the first answer. */
  const months = quote?.months || FREQ_MONTHS[form.frequency] || 3;
  const term = TERM[months] || TERM[3];

  const start = form.start_date ? new Date(`${form.start_date}T00:00:00`) : today;
  const end = new Date(start); end.setFullYear(end.getFullYear() + 1); end.setDate(end.getDate() - 1);
  const periodDays = term.days;
  const nextDue = new Date(start); nextDue.setDate(nextDue.getDate() + periodDays);

  /* The backend cannot resolve these — it does not know the prospect. Every
     string passes through here before it is rendered, HTML included. */
  const fill = (text) =>
    typeof text === "string"
      ? text
          .replace(/\{\{client_name\}\}/g, client)
          .replace(/\{\{contact_name\}\}/g, form.contact_name?.trim() || "")
          .replace(/\{\{marks\}\}/g, num(quote?.marks))
          .replace(/\{\{brand_name\}\}/g, company.brand_name || "")
          .replace(/\{\{plan\}\}/g, quote?.plan || "")
          .replace(/\{\{validity_days\}\}/g, String(validity))
      : "";

  return {
    company, client, seg, audience, today, validity, validUntil,
    /* Printed with its country code: a bare number is only unambiguous to
       whoever typed it. */
    clientPhone: withCountryCode(form.country_code, form.phone),
    isCustom, p, li, d, months,
    termLabel: term.label,
    termWord: term.word,
    firstLabel: term.first,
    periodDays,
    grace: term.grace,
    start, end, nextDue,
    /* Both discounts land in one amount, so they are shown as one line with both
       percentages named rather than split into figures the API never gave. */
    totalDiscPct: (d.auto_percent || 0) + (d.additional_percent || 0),
    discountLabel: [
      d.auto_percent ? `${d.auto_percent}% prepay` : "",
      d.additional_percent ? `${d.additional_percent}% additional` : "",
    ].filter(Boolean).join(" + "),
    /* Only a per-trademark segment quotes a rate — a flat-tier plan is priced by
       band, and printing a rate there would invite the wrong comparison. */
    showRateRow: li.per_mark_rate_yr != null,
    /* Two figures the API gives, differenced — not a price this screen decides. */
    onboardingSaving: Math.max(0, (p.onboarding || 0) - (p.onboarding_net || 0)),
    fill,
  };
};

/**
 * The prospect's details have to be filled in before either document can be
 * addressed to anyone. Returns the missing labels, or an empty list.
 */
export const missingClientFields = (form) => {
  const missing = [];
  if (!form.client_name?.trim()) missing.push("client / company name");
  if (!form.contact_name?.trim()) missing.push("contact name");
  if (!form.email?.trim()) missing.push("contact email");
  if (!form.phone?.trim()) missing.push("contact phone");
  return missing;
};
